// Third party
import React, { Component } from 'react';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { Grid, Row, Col, Image } from 'react-bootstrap';
import ReactHtmlParser from 'react-html-parser';

// GDC standards
import Heading from '../../components/gdc-components-atoms-heading';
import Button from '../../components/gdc-components-atoms-button';
import Input from '../../components/common/input';

// Components
import propTypes from 'prop-types';
import proofTypes from './components/proof-of-ownership/proofTypes';
import ProofOfOwnerShip from './components/proof-of-ownership';
import ProofOfOwnershipMobile from './components/proof-of-ownership-mobile';
import serviceMessages from './serviceMessages';

// Others
import { forceIeEdgeReRender } from '../../utils/cacheUtil';
import googleAnalyticsKeys from '../../utils/googleAnalyticsKeys';
import * as googleAnalyticsUtil from '../../utils/googleAnalyticsUtil';
import * as actions from './actions';

import './styles.css';

export class AccountConnector extends Component {
    componentDidMount() {
        // Fix dirty cache issue on IE/Edge browser until we find a better solution.
        const { userName, password, proofOfOwnershipValue } = this.props.accountConnector;
        const inputs = [
            { id: 'user-name-input', value: userName },
            { id: 'password-input', value: password },
            { id: 'proof-of-owner-ship-input', value: proofOfOwnershipValue }
        ];
        forceIeEdgeReRender(inputs);
    }

    componentDidUpdate() {
        // Lam Nguyen 2019-07-18:
        // We need to refactor this to use callback pattern to improve performance
        // because componentDidUpdate() function is triggered continuously when there are any changes in props.
        // Refer to Solera Rewards UI project, shop-info module, saveProfileAndConnectWithDirectHit() function for how to do it.
        // Since our current priority is resolving Cross Domain Tracking issue, I will not do it now.
        // We need to revisit this when time allows (expect to happen by the end of sprint "DHW Sprint Ending 2019-07-26").

        const callbackUrl = this.props.configuration.connectParams.callbackUrl;
        const linkerParam = googleAnalyticsUtil.getLinkerParam();
        if (this.props.accountConnector.isSubmitSuccessful) {
            window.location.href = `${callbackUrl}link-success?${linkerParam}`;
        }
        if (this.props.accountConnector.navigateBack) {
            window.location.assign(`${callbackUrl}shop-info?${linkerParam}`);
        }
    }

    userNameChangedHandler = (value) => {
        this.props.actions.changeUserName(value);
    }

    passwordChangedHandler = (value) => {
        this.props.actions.changePassword(value);
    }

    customerNumberSelectedHandler = () => {
        const isCustomerNumberSelected = this.props.accountConnector.proofOfOwnershipType === proofTypes.CUSTOMER_NUMBER;
        if (!isCustomerNumberSelected) {
            this.props.actions.changeProofOfOwnershipType(proofTypes.CUSTOMER_NUMBER);
        }
    }

    creditCardSelectedHandler = () => {
        const isCardNumberSelected = this.props.accountConnector.proofOfOwnershipType === proofTypes.LAST_FOUR_CREDIT_CARD;

        if (!isCardNumberSelected) {
            this.props.actions.changeProofOfOwnershipType(proofTypes.LAST_FOUR_CREDIT_CARD);
        }
    }

    proofOfOwnerShipValueChangedHandler = (event) => {
        this.props.actions.changeProofOfOwnershipValue(event.target.value);
    }

    submitForm = () => {
        googleAnalyticsUtil.raiseEvent({
            category: googleAnalyticsKeys.categories.FORWARD_BUTTON_NAVIGATION,
            action: googleAnalyticsKeys.actions.CLICK_DIRECT_HIT_SUBMIT_BUTTON
        });
        this.props.actions.submit();
    }

    handleOnGoBack = () => {
        googleAnalyticsUtil.raiseEvent({
            category: googleAnalyticsKeys.categories.BACKWARD_BUTTON_NAVIGATION,
            action: googleAnalyticsKeys.actions.CLICK_DIRECT_HIT_BACK_BUTTON
        });
        this.props.actions.navigateBack(true);
    }

    keypress = (event) => {
        if (event && event.which && event.which === 13 && this.props.accountConnector.isFormComplete === true) {
            this.submitForm();
        }
    }

    displayError() {
        const validationErrors = this.props.accountConnector.validationErrors;
        const baseUrl = this.props.configuration.connectParams.callbackUrl;
        const replaceContactUs = validationError => validationError.replace(/\|\|contact-us\|\|/gi, `${baseUrl}contact-us`)
        const displayErrorMessages = () => {
            if (validationErrors.length === 1) {
                return ReactHtmlParser(replaceContactUs(validationErrors[0]));
            }
            else {
                return (
                    <ul id="error-message-summary">
                        {validationErrors.map((validationError, index) => {
                            return (<li id={`error-message-${index + 1}`} key={`error-message-${index + 1}`}>{ReactHtmlParser(replaceContactUs(validationError))}</li>);
                        })}
                    </ul>
                );
            }
        };

        if (validationErrors && validationErrors.length > 0) {
            return (
                <Col xs={12} className="error-form">
                    <Col className="alert-text">
                        <Col id="error-icon" xsHidden sm={1} className="col-6p">
                            <Image className="icon-error" src="/images/icon-error.svg" alt="Error" />
                        </Col>
                        <Col id="error-message" xs={12} sm={11}>
                            {displayErrorMessages()}
                        </Col>
                    </Col>
                </Col>
            )
        }
        return null;
    }

    displayLoading(isProcessing) {
        if (isProcessing) {
            return (
                <Image id="spinner-loading" className="spinner-loading" src="/images/ajax-loader.gif" alt="Loading" />
            );
        }
        return null;
    }

    // This function determines whether to show the red-border and exclamation icon for error fields.
    getFieldErrorsFromServiceMessages() {
        const validationErrors = this.props.accountConnector.validationErrors;
        if (validationErrors && validationErrors.length > 0) {
            switch (validationErrors[0]) {
                case serviceMessages.AUTHENTICATION_FAILED:
                    return {
                        isUserCredentialsError: true,
                        isProofOfOwnershipError: false
                    };
                case serviceMessages.CONFLICT_SHOP_ID:
                    return {
                        isUserCredentialsError: true,
                        isProofOfOwnershipError: true
                    };
                case serviceMessages.INVALID_IDENTIFIX_CUSTOMER_NUMBER:
                    return {
                        isUserCredentialsError: false,
                        isProofOfOwnershipError: true
                    };
                default:
                    break;
            }
        }
        return {
            isUserCredentialsError: false,
            isProofOfOwnershipError: false
        };
    }

    render() {
        const { isProcessing } = this.props.accountConnector;
        const isCardNumberSelected = this.props.accountConnector.proofOfOwnershipType === proofTypes.LAST_FOUR_CREDIT_CARD;
        const isCustomerNumberSelected = this.props.accountConnector.proofOfOwnershipType === proofTypes.CUSTOMER_NUMBER;
        const proofOfOwnershipPlaceholder = (isCardNumberSelected) ? 'Enter last 4 digits of card number' : 'Enter Identifix Customer Number';
        const fieldErrors = this.getFieldErrorsFromServiceMessages();

        return (
            <Grid bsClass="solera-direct-hit-background">
                <Grid bsClass="solera-container">
                    <ul id="enrollment-tab-lines" className="list-inline">
                        <li className="inline-block tab-line"></li>
                        <li className="inline-block tab-line"></li>
                        <li className="inline-block tab-line tab-line-active"></li>
                    </ul>
                    <Heading id="account-connector-form-title" className="solera-title-h2">Direct-Hit Account Information</Heading>
                    <Col id="account-connector-form-decription" md={10} sm={12} bsClass="solera-title-h4">Please enter your Identifix Direct-Hit credentials and customer number or
                        the last four digits of the credit card on file to connect your account to Solera Rewards.
                    </Col>
                    {this.displayError()}
                    <Grid bsClass="input-form">
                        <Row>
                            <Col sm={6}>
                                <Input
                                    labelId="user-name-label"
                                    labelValue="User Name"
                                    inputId="user-name-input"
                                    tabIndex="1"
                                    mandatory={true}
                                    maxLength="320"
                                    placeholder="Enter User Name"
                                    inputValue={this.props.accountConnector.userName}
                                    handleOnKeyPress={this.keypress}
                                    handleOnChangeValue={this.userNameChangedHandler}
                                    isError={fieldErrors.isUserCredentialsError}
                                />
                            </Col>
                            <Col sm={6} xsHidden>
                                <ProofOfOwnerShip
                                    id='proof-of-ownership'
                                    isError={fieldErrors.isProofOfOwnershipError}
                                    tabIndex="3"
                                    customerNumberSelected={isCustomerNumberSelected}
                                    customerNumberSelectedHandler={this.customerNumberSelectedHandler}
                                    creditCardSelected={isCardNumberSelected}
                                    creditCardSelectedHandler={this.creditCardSelectedHandler}
                                    proofOfOwnershipPlaceholder={proofOfOwnershipPlaceholder}
                                    proofOfOwnerShipValue={this.props.accountConnector.proofOfOwnershipValue}
                                    proofOfOwnerShipValueChangedHandler={this.proofOfOwnerShipValueChangedHandler}
                                    onKeyPress={this.keypress}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col sm={6}>
                                <Col>
                                    <Input
                                        labelId="password-label"
                                        labelValue="Password"
                                        inputId="password-input"
                                        type="password"
                                        maxLength="20"
                                        tabIndex="2"
                                        inputType="password"
                                        mandatory={true}
                                        placeholder="Enter Password"
                                        inputValue={this.props.accountConnector.password}
                                        handleOnKeyPress={this.keypress}
                                        handleOnChangeValue={this.passwordChangedHandler}
                                        isError={fieldErrors.isUserCredentialsError}
                                    />
                                </Col>
                            </Col>
                            <Col sm={6} className="visible-xs">
                                <ProofOfOwnershipMobile
                                    id='proof-of-ownership-mobile'
                                    tabIndex="3"
                                    isError={fieldErrors.isProofOfOwnershipError}
                                    customerNumberSelected={isCustomerNumberSelected}
                                    customerNumberSelectedHandler={this.customerNumberSelectedHandler}
                                    creditCardSelected={isCardNumberSelected}
                                    creditCardSelectedHandler={this.creditCardSelectedHandler}
                                    proofOfOwnershipPlaceholder={proofOfOwnershipPlaceholder}
                                    proofOfOwnerShipValue={this.props.accountConnector.proofOfOwnershipValue}
                                    proofOfOwnerShipValueChangedHandler={this.proofOfOwnerShipValueChangedHandler}
                                    onKeyPress={this.keypress}
                                />
                            </Col>
                        </Row>
                        <Row>
                            <Col xs={12} className="solera-direct-hit-position-button">
                                {this.displayLoading(isProcessing)}
                                <Button
                                    type="button"
                                    tabIndex="5"
                                    id="go-back-button"
                                    className="back-button"
                                    disabled={isProcessing}
                                    onClick={this.handleOnGoBack}>
                                    Back
                                </Button>
                                <Button
                                    type="button"
                                    tabIndex="4"
                                    id="connect-submit-button"
                                    className="next-button"
                                    disabled={isProcessing ? true : !this.props.accountConnector.isFormComplete}
                                    onClick={this.submitForm}>
                                    Submit
                                </Button>
                            </Col>
                        </Row>
                    </Grid>
                </Grid>
            </Grid>
        );
    }
}

AccountConnector.propTypes = {
    proofOfOwnershipType: propTypes.string.isRequired,
    validationErrors: propTypes.array,
    userName: propTypes.string,
    password: propTypes.string,
    proofOfOwnershipValue: propTypes.string,
    isFormComplete: propTypes.bool.isRequired,
    isProcessing: propTypes.bool,
    isSubmitSuccessful: propTypes.bool,
    navigateBack: propTypes.bool
};

AccountConnector.defaultProps = {
    proofOfOwnershipType: proofTypes.CUSTOMER_NUMBER,
    isFormComplete: false
};

// This is the complete example of connected component using: mapStateToProps, mapDispatchToProps, mergeProps.
export default connect(
    store => ({
        accountConnector: store.accountConnector,
        configuration: store.configuration
    }),
    dispatch => ({
        actions: bindActionCreators(actions, dispatch)
    })
)(AccountConnector);



// WEBPACK FOOTER //
// ./src/modules/account-connector/index.js